import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TaskWeekBoard — "what needs to get done THIS WEEK, at a glance."
 * docs/TODO-FUNNEL-VISION.md §3.4 (Ryan, 2026-08-07).
 *
 * A kanban whose columns are TIME, not lists: Overdue, then each remaining
 * day of the week (today highlighted), then an Unscheduled pool of open
 * next-actions/projects with no date. Dragging a card onto a day SETS ITS
 * DUE DATE — planning the week is a minute of drag-drop. Dropping on
 * Unscheduled clears the date. Check dot completes.
 *
 * Meetings overlay (Ryan, 2026-09-08): a toggle paints the viewer's
 * calendar meetings as a bullet list at the TOP of each day column, tasks
 * below — so "what needs doing" and "where it fits between meetings" sit
 * in one glance. The board never reads a calendar itself: the host hands
 * it `onLoadMeetings` (TekStak → /api/calendar/events; GAS → CalendarApp,
 * in-tenant) and the list is render-only, never persisted.
 *
 * Presentational + MUI-free; shares the token scope and callbacks with
 * TaskBubbleCanvas/TaskMatrix.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors, } from '@dnd-kit/core';
import { CalendarClock, Check, Sun } from 'lucide-react';
import { BubbleFocus, TaskBulkBar, TaskQuickAdd, useUndoableTasks, useOptimisticShelf, taskScore, priorityBand, groupByAccount } from '../TaskBubbleCanvas/TaskBubbleCanvas.js';
const DAY_MS = 86400000;
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function localIso(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function dueIso(t) {
    if (!t.due_date)
        return null;
    const m = String(t.due_date).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : null;
}
function buildColumns() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cols = [{ key: 'overdue', label: 'Overdue' }];
    // Today through Sunday (planning horizon = the rest of this week).
    const daysLeft = 7 - today.getDay(); // Sun=0 → 7 (whole week ahead on Sundays)
    const span = Math.max(daysLeft, 3); // never fewer than 3 day columns
    for (let i = 0; i < span; i++) {
        const d = new Date(today.getTime() + i * DAY_MS);
        cols.push({
            key: localIso(d),
            label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : DOW[d.getDay()],
            sub: `${d.getMonth() + 1}/${d.getDate()}`,
            today: i === 0,
        });
    }
    cols.push({ key: 'none', label: 'Unscheduled' });
    return cols;
}
const PRIO_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };
/* ── Meetings overlay helpers ─────────────────────────────────────────── */
const MEETINGS_PREF_KEY = 'tekstak.weekBoard.meetings';
const MEETINGS_REFRESH_MS = 5 * 60 * 1000;
function readMeetingsPref(key) {
    try {
        return window.localStorage.getItem(key) === '1';
    }
    catch {
        return false;
    }
}
function writeMeetingsPref(key, on) {
    try {
        window.localStorage.setItem(key, on ? '1' : '0');
    }
    catch { /* private mode / blocked storage */ }
}
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
/** "9a", "9:30a", "1p" — compact enough for a 158px column. */
function fmtClock(d) {
    let h = d.getHours();
    const m = d.getMinutes();
    const ap = h >= 12 ? 'p' : 'a';
    h = h % 12 || 12;
    return m ? `${h}:${String(m).padStart(2, '0')}${ap}` : `${h}${ap}`;
}
function meetingMinutes(m) {
    if (m.all_day || !m.end)
        return 0;
    const ms = new Date(m.end).getTime() - new Date(m.start).getTime();
    return Number.isFinite(ms) && ms > 0 ? Math.round(ms / 60000) : 0;
}
function fmtHours(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (!h)
        return `${m}m`;
    return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}
/** Bucket by LOCAL day; all-day spans paint on every day they cover
 *  (Google's all-day `end` is exclusive). Declined entries are dropped. */
function bucketMeetings(meetings, dayKeys) {
    const out = {};
    for (const k of dayKeys)
        out[k] = [];
    for (const m of meetings) {
        if (!m || m.declined || !m.start)
            continue;
        if (m.all_day) {
            const startIso = DATE_ONLY.test(m.start) ? m.start : localIso(new Date(m.start));
            const endIso = m.end && DATE_ONLY.test(m.end) ? m.end : m.end ? localIso(new Date(m.end)) : null;
            const cur = new Date(`${startIso}T00:00:00`);
            const stop = endIso && endIso > startIso ? new Date(`${endIso}T00:00:00`) : new Date(cur.getTime() + DAY_MS);
            for (let d = cur; d < stop; d = new Date(d.getTime() + DAY_MS)) {
                const k = localIso(d);
                if (out[k])
                    out[k].push(m);
            }
        }
        else {
            const d = new Date(m.start);
            if (Number.isNaN(d.getTime()))
                continue;
            const k = localIso(d);
            if (out[k])
                out[k].push(m);
        }
    }
    for (const k of dayKeys) {
        out[k].sort((a, b) => {
            const ad = a.all_day ? 0 : 1;
            const bd = b.all_day ? 0 : 1;
            return ad - bd || String(a.start).localeCompare(String(b.start));
        });
    }
    return out;
}
function MeetingList({ meetings, label }) {
    const now = Date.now();
    return (_jsxs("div", { className: "twk-mtgs", "aria-label": `Meetings ${label}`, children: [_jsxs("span", { className: "twk-mtgs-head", children: [_jsx(CalendarClock, { size: 10, "aria-hidden": true }), "Meetings", meetings.length ? ` · ${meetings.length}` : ''] }), meetings.length ? (_jsx("ul", { className: "twk-mtg-list", children: meetings.map((m) => {
                    const startMs = m.all_day ? NaN : new Date(m.start).getTime();
                    const endMs = m.all_day || !m.end ? NaN : new Date(m.end).getTime();
                    const live = !m.all_day && startMs <= now && now < endMs;
                    const past = !m.all_day && Number.isFinite(endMs) && endMs <= now;
                    const when = m.all_day
                        ? 'All day'
                        : `${fmtClock(new Date(m.start))}${m.end ? `–${fmtClock(new Date(m.end))}` : ''}`;
                    const tip = [when, m.title, m.location].filter(Boolean).join(' · ');
                    return (_jsxs("li", { className: ['twk-mtg', m.all_day ? 'twk-mtg-allday' : '', live ? 'twk-mtg-now' : '', past ? 'twk-mtg-past' : ''].filter(Boolean).join(' '), title: tip, children: [_jsx("span", { className: "twk-mtg-time", children: when }), m.link ? (_jsx("a", { className: "twk-mtg-title", href: m.link, target: "_blank", rel: "noreferrer", onPointerDown: (e) => e.stopPropagation(), children: m.title })) : (_jsx("span", { className: "twk-mtg-title", children: m.title }))] }, m.id));
                }) })) : (_jsx("span", { className: "twk-mtgs-empty", children: "No meetings \u2014 open day" })), _jsx("span", { className: "twk-mtgs-rule", "aria-hidden": true })] }));
}
function WeekCard({ task, dragging, overlay, completing, onCheck, onReopen, onExpand, isSelected, onToggleSelect, }) {
    const done = task.status === 'completed';
    const drag = useDraggable({ id: `wk:${task.id}`, disabled: overlay || done });
    const edgeClass = task.context ? `tbc-ctx-${(task.context || '').replace('@', '')}` : 'tbc-ctx-none';
    return (_jsxs("div", { ref: drag.setNodeRef, className: [
            'twk-card',
            `tbc-band-${priorityBand(taskScore(task))}`,
            edgeClass,
            `twk-prio-${task.priority || 'medium'}`,
            dragging ? 'twk-dragging' : '',
            overlay ? 'twk-overlay' : '',
            completing ? 'twk-completing' : '',
            done ? 'twk-done' : '',
            task.archived_at ? 'twk-archived' : '',
            isSelected ? 'twk-selected' : '',
        ].filter(Boolean).join(' '), ...drag.attributes, ...drag.listeners, onClick: (e) => {
            if ((e.shiftKey || e.metaKey) && onToggleSelect) {
                e.stopPropagation();
                onToggleSelect(task.id);
                return;
            }
            onExpand?.(task.id, e.currentTarget.getBoundingClientRect());
        }, children: [!overlay && onToggleSelect && (_jsx("button", { type: "button", className: `tbc-sel twk-sel ${isSelected ? 'tbc-sel-on' : ''}`, role: "checkbox", "aria-checked": !!isSelected, "aria-label": isSelected ? 'Deselect task' : 'Select task', title: isSelected ? 'Deselect' : 'Select for bulk actions', onClick: (e) => { e.stopPropagation(); onToggleSelect(task.id); }, onPointerDown: (e) => e.stopPropagation() })), _jsx("button", { type: "button", className: `twk-check ${done ? 'twk-check-done' : ''}`, "aria-label": done ? 'Reopen task' : 'Complete task', title: done ? 'Done — click to reopen' : 'Complete', onClick: (e) => {
                    e.stopPropagation();
                    if (done)
                        onReopen?.(task.id);
                    else
                        onCheck?.(task.id);
                }, onPointerDown: (e) => e.stopPropagation(), children: _jsx(Check, { size: 12, strokeWidth: 3, "aria-hidden": true }) }), _jsx("span", { className: "twk-title", children: task.title }), _jsxs("span", { className: "twk-meta", children: [task.today && _jsx(Sun, { size: 10, className: "twk-today", "aria-label": "In today's plan" }), task.account_label && _jsx("span", { className: "twk-tag", children: task.account_label }), task.waiting_for_who && _jsx("span", { className: "twk-tag twk-tag-person", children: task.waiting_for_who })] })] }));
}
export function TaskWeekBoard({ tasks: tasksProp, onUpdate, onComplete, onReopen, onAttach, onAttachFile, onGetContextUrl, onLoadContext, onRemoveContext, onDelete, onCreate, onArchive, onUnarchive, onBulkArchive, onAssign, onCreatePerson, accounts, people, onLogEvents, onReframe, onAcceptTriage, onLoadMeetings, meetingsPrefKey, }) {
    // Keyboard path (UX P2-8) mirrors the canvas: Space/Enter lift, arrows move.
    // Touch mirrors the canvas too (phone pass 2026-08-12): long-press lifts,
    // flicks scroll, taps select.
    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }), useSensor(KeyboardSensor));
    // `tasks` from here on carries the optimistic field overlay — edits paint
    // on the board instantly (UI-responsiveness review 2026-08-08).
    const { tasks, update: undoableUpdate, complete: undoableComplete, undo, canUndo } = useUndoableTasks(tasksProp, onUpdate, onComplete, onReopen);
    const [activeId, setActiveId] = useState(null);
    // INT-3 (audit): overrides carry the task's updated_at at move time; when
    // the parent delivers fresher server state, the override clears itself.
    const [overrides, setOverrides] = useState({});
    React.useEffect(() => {
        setOverrides((o) => {
            const keys = Object.keys(o);
            if (!keys.length)
                return o;
            let changed = false;
            const next = {};
            for (const id of keys) {
                const t = tasks.find((x) => x.id === id);
                if (t && (t.updated_at ?? null) === o[id].base)
                    next[id] = o[id];
                else
                    changed = true;
            }
            return changed ? next : o;
        });
    }, [tasks]);
    const [completing, setCompleting] = useState({});
    const [gone, setGone] = useState({});
    // Archive/unarchive leave the board the instant they're clicked (shared
    // optimistic mechanism — request behind, revert on reject).
    const shelf = useOptimisticShelf(tasks, { onArchive, onUnarchive, onBulkArchive });
    const [expanded, setExpanded] = useState(null);
    const [selected, setSelected] = useState({});
    const selectedIds = Object.keys(selected).filter((k) => selected[k]);
    const clearSelection = () => setSelected({});
    React.useEffect(() => {
        if (!selectedIds.length)
            return;
        const onKey = (e) => { if (e.key === 'Escape')
            clearSelection(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedIds.length]);
    const columns = useMemo(buildColumns, []);
    const todayIso = columns.find((c) => c.today)?.key || localIso(new Date());
    // Meetings overlay — toggle remembered per browser; the host's loader
    // runs on toggle-on and every few minutes while on. The latest loader
    // lives in a ref so an unstable host callback can't become a fetch storm.
    const prefKey = meetingsPrefKey || MEETINGS_PREF_KEY;
    const canShowMeetings = !!onLoadMeetings;
    const [showMeetings, setShowMeetings] = useState(() => canShowMeetings && readMeetingsPref(prefKey));
    const [meetings, setMeetings] = useState([]);
    const [meetingsStatus, setMeetingsStatus] = useState('idle');
    const [meetingsError, setMeetingsError] = useState(null);
    const loaderRef = useRef(onLoadMeetings);
    loaderRef.current = onLoadMeetings;
    const dayKeys = useMemo(() => columns.filter((c) => c.key !== 'overdue' && c.key !== 'none').map((c) => c.key), [columns]);
    const toggleMeetings = useCallback(() => {
        setShowMeetings((on) => {
            writeMeetingsPref(prefKey, !on);
            return !on;
        });
    }, [prefKey]);
    useEffect(() => {
        if (!showMeetings || !canShowMeetings || !dayKeys.length)
            return undefined;
        let alive = true;
        let hadData = false;
        const run = () => {
            const load = loaderRef.current;
            if (!load)
                return;
            if (!hadData)
                setMeetingsStatus('loading');
            Promise.resolve()
                .then(() => load({ start: dayKeys[0], end: dayKeys[dayKeys.length - 1] }))
                .then((rows) => {
                if (!alive)
                    return;
                hadData = true;
                setMeetings(Array.isArray(rows) ? rows : []);
                setMeetingsError(null);
                setMeetingsStatus('ready');
            })
                .catch((e) => {
                if (!alive)
                    return;
                setMeetingsError(String((e && e.message) || e || 'Calendar unavailable'));
                setMeetingsStatus('error');
            });
        };
        run();
        const timer = window.setInterval(run, MEETINGS_REFRESH_MS);
        return () => { alive = false; window.clearInterval(timer); };
    }, [showMeetings, canShowMeetings, dayKeys]);
    const meetingsByDay = useMemo(() => bucketMeetings(meetings, dayKeys), [meetings, dayKeys]);
    const busyByDay = useMemo(() => {
        const out = {};
        for (const k of dayKeys)
            out[k] = (meetingsByDay[k] || []).reduce((sum, m) => sum + meetingMinutes(m), 0);
        return out;
    }, [meetingsByDay, dayKeys]);
    const meetingsOn = showMeetings && canShowMeetings;
    const weekMeetingCount = meetingsOn ? dayKeys.reduce((n, k) => n + (meetingsByDay[k] || []).length, 0) : 0;
    const weekBusyMinutes = meetingsOn ? dayKeys.reduce((n, k) => n + (busyByDay[k] || 0), 0) : 0;
    const byColumn = useMemo(() => {
        const grouped = {};
        for (const c of columns)
            grouped[c.key] = [];
        const active = tasks
            .filter((t) => !t.status || t.status !== 'cancelled')
            .filter((t) => !gone[t.id] && !(t.id in shelf.hidden))
            .filter((t) => t.gtd_list !== 'someday_maybe' && t.gtd_list !== 'reference' && !t.project_id);
        for (const t of active) {
            const ov = overrides[t.id]?.col;
            const iso = ov !== undefined ? (ov === 'none' ? null : ov === 'overdue' ? dueIso(t) : ov) : dueIso(t);
            let key;
            if (ov === 'overdue')
                key = 'overdue';
            else if (!iso)
                key = 'none';
            else if (iso < todayIso)
                key = 'overdue';
            else if (grouped[iso])
                key = iso;
            else
                continue; // due beyond this week — not this board's business
            grouped[key].push(t);
        }
        // Stack by importance (heat score, hottest on top); completed sink to
        // the bottom of their day, struck through.
        for (const key of Object.keys(grouped)) {
            grouped[key].sort((a, b) => {
                const doneA = a.status === 'completed' ? 1 : 0;
                const doneB = b.status === 'completed' ? 1 : 0;
                return doneA - doneB || taskScore(b) - taskScore(a) || (a.position || 0) - (b.position || 0);
            });
        }
        return grouped;
    }, [tasks, columns, todayIso, overrides, gone, shelf.hidden]);
    const activeTask = activeId ? tasks.find((t) => t.id === activeId) : null;
    // Delete vanishes instantly too (canvas parity): hide, fire, revert on
    // reject — the host's error strip explains any return.
    const handleDelete = onDelete
        ? (id) => {
            setGone((g) => ({ ...g, [id]: true }));
            Promise.resolve(onDelete(id)).catch(() => {
                setGone((g) => {
                    const next = { ...g };
                    delete next[id];
                    return next;
                });
            });
        }
        : undefined;
    const handleCheck = (id) => {
        setCompleting((c) => ({ ...c, [id]: true }));
        window.setTimeout(() => {
            setCompleting((c) => {
                const next = { ...c };
                delete next[id];
                return next;
            });
            Promise.resolve(undoableComplete?.(id)).catch(() => { });
        }, 380);
    };
    const handleDragEnd = (e) => {
        const id = String(e.active.id).replace('wk:', '');
        setActiveId(null);
        if (!e.over || !onUpdate)
            return;
        if (!String(e.over.id).startsWith('wkcol:'))
            return;
        const colKey = String(e.over.id).replace('wkcol:', '');
        if (colKey === 'overdue')
            return; // you can't schedule something INTO overdue
        const newIso = colKey === 'none' ? null : colKey;
        const task = tasks.find((t) => t.id === id);
        if (!task || dueIso(task) === newIso)
            return;
        setOverrides((o) => ({ ...o, [id]: { col: colKey, base: task.updated_at ?? null } }));
        Promise.resolve(undoableUpdate(id, { due_date: newIso })).catch(() => setOverrides((o) => {
            const next = { ...o };
            delete next[id];
            return next;
        }));
    };
    return (_jsxs("div", { className: `twk ${selectedIds.length ? 'tbc-selmode' : ''}`, children: [canShowMeetings && (_jsxs("div", { className: "twk-bar", children: [_jsxs("button", { type: "button", className: `twk-toggle ${meetingsOn ? 'twk-toggle-on' : ''}`, "aria-pressed": meetingsOn, onClick: toggleMeetings, title: meetingsOn
                            ? 'Hide calendar meetings'
                            : 'Show your calendar meetings at the top of each day — see where the work fits', children: [_jsx(CalendarClock, { size: 13, "aria-hidden": true }), _jsx("span", { children: "Meetings" })] }), meetingsOn && meetingsStatus === 'loading' && (_jsx("span", { className: "twk-bar-note", role: "status", children: "Reading your calendar\u2026" })), meetingsOn && meetingsStatus === 'error' && (_jsx("span", { className: "twk-bar-note twk-bar-err", role: "status", children: meetingsError })), meetingsOn && meetingsStatus === 'ready' && (_jsx("span", { className: "twk-bar-note", role: "status", children: weekMeetingCount ? `${weekMeetingCount} meeting${weekMeetingCount === 1 ? '' : 's'} · ${fmtHours(weekBusyMinutes)} booked` : 'No meetings on the board' }))] })), _jsxs(DndContext, { sensors: sensors, collisionDetection: pointerWithin, onDragStart: (e) => setActiveId(String(e.active.id).replace('wk:', '')), onDragEnd: handleDragEnd, children: [_jsx("div", { className: "twk-grid", children: columns.map((col) => {
                            const colTasks = byColumn[col.key] || [];
                            const isDay = col.key !== 'overdue' && col.key !== 'none';
                            const dayMeetings = meetingsOn && isDay && meetingsStatus !== 'error' ? (meetingsByDay[col.key] || []) : null;
                            // Vertical account grouping via the ONE shared helper (audit §5):
                            // General (no-account) group first, then alphabetical accounts.
                            const groups = groupByAccount(colTasks, { order: 'alpha' });
                            const showHeads = groups.some((g) => g.label);
                            return (_jsx(WeekColumn, { col: col, count: colTasks.length, busyMinutes: dayMeetings ? busyByDay[col.key] || 0 : 0, meetings: dayMeetings && meetingsStatus === 'ready' ? (_jsx(MeetingList, { meetings: dayMeetings, label: `${col.label}${col.sub ? ` ${col.sub}` : ''}` })) : undefined, quickAdd: onCreate && col.key !== 'overdue' ? (_jsx(TaskQuickAdd, { title: col.key === 'none' ? 'New unscheduled task' : `New task due ${col.label}`, placeholder: col.key === 'none' ? 'New task…' : `Due ${col.label}…`, onSubmit: (title) => onCreate({ title, due_date: col.key === 'none' ? null : col.key }) })) : undefined, children: groups.map((g) => (_jsxs(React.Fragment, { children: [showHeads && (_jsx("span", { className: `twk-acct-head ${g.label ? '' : 'twk-acct-head-none'}`, children: g.label || 'General' })), g.tasks.map((t) => (_jsx(WeekCard, { task: t, dragging: t.id === activeId, completing: !!completing[t.id], onCheck: handleCheck, onReopen: onReopen, onExpand: (id, anchor) => setExpanded({ id, anchor }), isSelected: !!selected[t.id], onToggleSelect: (id) => setSelected((s2) => ({ ...s2, [id]: !s2[id] })) }, t.id)))] }, g.label || '(none)'))) }, col.key));
                        }) }), _jsx(DragOverlay, { dropAnimation: null, children: activeTask ? _jsx(WeekCard, { task: activeTask, overlay: true }) : null })] }), _jsx(TaskBulkBar, { tasks: tasks.filter((t) => selected[t.id]), people: people, moveOptions: columns.filter((c) => c.key !== 'overdue').map((c) => ({ key: c.key, label: c.key === 'none' ? 'Unscheduled' : `${c.label}${c.sub ? ` ${c.sub}` : ''}` })), moveLabel: "Move to day", onMove: onUpdate ? (id, key) => onUpdate(id, { due_date: key === 'none' ? null : key }) : undefined, onComplete: onComplete ? (id) => handleCheck(id) : undefined, onArchive: shelf.archive, onBulkArchive: shelf.archiveMany, onDelete: handleDelete, onAssign: onAssign, onLogEvents: onLogEvents, onClear: clearSelection }), expanded && (() => {
                const t = tasks.find((x) => x.id === expanded.id);
                if (!t)
                    return null;
                return (_jsx(BubbleFocus, { task: t, anchor: expanded.anchor, onReframe: onReframe, onAcceptTriage: onAcceptTriage, onClose: () => setExpanded(null), onUndo: undo, canUndo: canUndo, onCheck: handleCheck, onReopen: onReopen, onUpdate: undoableUpdate, onAttach: onAttach, onAttachFile: onAttachFile, onGetContextUrl: onGetContextUrl, onLoadContext: onLoadContext, onRemoveContext: onRemoveContext, onDelete: handleDelete, onArchive: shelf.archive, onUnarchive: shelf.unarchive, onAssign: onAssign, onCreatePerson: onCreatePerson, accounts: accounts, people: people }));
            })()] }));
}
function WeekColumn({ col, count, busyMinutes, meetings, quickAdd, children }) {
    const { setNodeRef, isOver } = useDroppable({ id: `wkcol:${col.key}` });
    return (_jsxs("section", { ref: setNodeRef, className: [
            'twk-col',
            col.key === 'overdue' ? 'twk-col-overdue' : '',
            col.key === 'none' ? 'twk-col-none' : '',
            col.today ? 'twk-col-today' : '',
            isOver ? 'twk-col-over' : '',
        ].filter(Boolean).join(' '), "aria-label": col.label, children: [_jsxs("header", { className: "twk-head", children: [_jsx("span", { className: "twk-label", children: col.label }), col.sub && _jsx("span", { className: "twk-sub", children: col.sub }), busyMinutes ? (_jsx("span", { className: "twk-busy", title: `${fmtHours(busyMinutes)} booked in meetings`, children: fmtHours(busyMinutes) })) : null, _jsx("span", { className: "twk-count", children: count })] }), _jsxs("div", { className: "twk-body", children: [meetings, children, quickAdd] })] }));
}
export default TaskWeekBoard;
